import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { entraLoginRequest } from '@/lib/msalConfig'
import { msalInstance } from '@/lib/msalInstance'
import { authService } from '@/services/api/auth.service'
import { acquireEntraAccessToken, hasActiveEntraSession } from '@/services/api/entraAuth.service'
import { meService } from '@/services/api/me.service'
import type { LoginResponse } from '@/types/auth.types'
import type { UserInfo } from '@/types/me.types'
import { type AuthMethod, tokenManager } from '@/utils/tokenManager'

import { AuthContext, type AuthContextType } from './AuthContext'

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const [authMethod, setAuthMethod] = useState<AuthMethod | null>(null)
  const navigate = useNavigate()

  const isAuthenticated = user !== null

  const clearSession = useCallback(() => {
    authService.clearSession()
    tokenManager.clearAll()
    setUser(null)
    setAuthMethod(null)
  }, [])

  const logout = useCallback(async () => {
    try {
      if (authMethod === 'entra') {
        // Revoca la sesión en Entra ID sin mostrar UI — onRedirectNavigate: false
        // evita que MSAL redirija el navegador al endpoint de logout de Microsoft.
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          await msalInstance.logoutRedirect({
            account: accounts[0],
            onRedirectNavigate: () => false
          })
        }
      } else {
        await authService.logout()
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearSession()
      navigate('/login', { replace: true })
    }
  }, [authMethod, clearSession, navigate])

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResponse> => {
      try {
        const loginResponse = await authService.login(email, password)
        tokenManager.setAuthMethod('own')
        setAuthMethod('own')

        const userInfo = await meService.getUserInfo()
        setUser(userInfo)

        return loginResponse
      } catch (error) {
        console.error('Login failed:', error)
        clearSession()
        throw error
      }
    },
    [clearSession]
  )

  const loginWithEntra = useCallback(async (): Promise<void> => {
    try {
      // Inicia el flujo OAuth PKCE con popup
      await msalInstance.loginPopup(entraLoginRequest)

      // Adquiere el access token para la API (scope api.access)
      await acquireEntraAccessToken()

      tokenManager.setAuthMethod('entra')
      setAuthMethod('entra')

      const userInfo = await meService.getUserInfo()
      setUser(userInfo)

      navigate('/dashboard', { replace: true })
    } catch (error) {
      console.error('Entra ID login failed:', error)
      clearSession()
      throw error
    }
  }, [clearSession, navigate])

  const refreshToken = useCallback(async () => {
    try {
      if (authMethod !== 'entra') {
        await authService.refreshToken()
      }
      // Para Entra el interceptor de Axios adquiere token fresco automáticamente.

      if (!user) {
        const userInfo = await meService.getUserInfo()
        setUser(userInfo)
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      clearSession()
      navigate('/login', { replace: true })
      throw error
    }
  }, [authMethod, user, clearSession, navigate])

  const getCurrentUser = useCallback(async (): Promise<UserInfo | null> => {
    try {
      const userInfo = await meService.getUserInfo()
      setUser(userInfo)
      return userInfo
    } catch (error) {
      console.error('Get current user failed:', error)
      clearSession()
      return null
    }
  }, [clearSession])

  const hasRoles = useCallback(
    (roles: string[]): boolean => {
      if (!user || !user.roles) return false
      return roles.some((role) => user.roles.includes(role))
    },
    [user]
  )

  useEffect(() => {
    const initAuth = async () => {
      if (initialized) return

      setIsLoading(true)

      try {
        const storedMethod = tokenManager.getAuthMethod()

        if (storedMethod === 'entra') {
          // Restaurar sesión de Entra ID si MSAL aún tiene una cuenta activa
          if (hasActiveEntraSession()) {
            try {
              await acquireEntraAccessToken()
              setAuthMethod('entra')
              const userInfo = await meService.getUserInfo()
              setUser(userInfo)
            } catch (error) {
              console.error('Entra session restore failed:', error)
              clearSession()
            }
          } else {
            // La sesión de MSAL expiró — limpiar estado local
            clearSession()
          }
        } else if (authService.isAuthenticated()) {
          // Restaurar sesión JWT propia
          if (tokenManager.shouldRefreshToken()) {
            try {
              await authService.refreshToken()
              setAuthMethod('own')
              const userInfo = await meService.getUserInfo()
              setUser(userInfo)
            } catch (error) {
              console.error('Token refresh failed:', error)
              clearSession()
            }
          } else {
            try {
              setAuthMethod('own')
              const userInfo = await meService.getUserInfo()
              setUser(userInfo)
            } catch (error) {
              console.error('Get current user failed:', error)
              clearSession()
            }
          }
        } else {
          clearSession()
        }
      } catch (error) {
        console.error('Auth initialization failed:', error)
        clearSession()
      } finally {
        setIsLoading(false)
        setInitialized(true)
      }
    }

    initAuth()
  }, [navigate, initialized, clearSession])

  // Redirect effect — separado de init
  useEffect(() => {
    if (!initialized || isLoading) return

    if (!isAuthenticated && window.location.pathname !== '/login') {
      navigate('/login', { replace: true })
    }
  }, [initialized, isLoading, isAuthenticated, navigate])

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    authMethod,
    hasRoles,
    isAdmin: () => hasRoles(['Administrador']),
    isManager: () => hasRoles(['Administrador', 'Gerente']),
    isConsultant: () => hasRoles(['Administrador', 'Gerente', 'Consultor']),
    isAnalyst: () => hasRoles(['Administrador', 'Gerente', 'Consultor', 'Analista']),
    login,
    loginWithEntra,
    logout,
    refreshToken,
    getCurrentUser,
    clearSession
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
